import React, { useState, useEffect, useCallback } from 'react';
import {
    X, ChevronRight, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Plus, Database, Search, FileJson, Terminal, TableProperties, Sparkles
} from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import SourceTypeSelectorModal from './SourceTypeSelectorModal';
import { apiService } from '../../../../../components/api/apiService';
import SchemaDiscoveryModal from './SchemaDiscoveryModal';
import QueryBuilderModal from './QueryBuilderModal';

SyntaxHighlighter.registerLanguage('json', json);

const STRUCTURED_DB_KEYS = ['postgresql', 'pgsql', 'postgres', 'sqlserver', 'mssql', 'mysql', 'oracle', 'snowflake', 'bigquery', 'redshift', 'sqlite'];
function isStructuredDatabase(connectorKey: string | undefined): boolean {
    if (!connectorKey) return false;
    const key = connectorKey.toLowerCase();
    return STRUCTURED_DB_KEYS.some(k => key.includes(k) || key === k);
}

type SourceDefinitionMode = 'schema' | 'custom_query';

/** API payload for ingestion entity creation / update */
export interface IngestionEntityPayload {
    entityName: string;
    description: string;
    sourceTypeUid: string;
    groupUuid: string;
    tags: string[];
    metadataStructure: string;
    isActive: boolean;
}

/** GET /DataModels/{id} response */
export interface DataModelResponse {
    id: string;
    modelName: string;
    description?: string;
    type?: string;
    status?: string;
    tags?: string[];
    metadataStructure?: string | object;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    updatedBy?: string;
    groupUuid?: string;
    /** Some APIs return snake_case */
    group_uuid?: string;
}

interface DataModelGroup {
    id: string;
    uuid: string;
    name: string;
}

interface Props {
    onClose: () => void;
    onSubmit: (data: IngestionEntityPayload) => void;
    submitting?: boolean;
    submitError?: string | null;
    /** When set, modal is in edit mode and form is prefilled from this data */
    initialData?: DataModelResponse | null;
    editId?: string | null;
    /** True while fetching the data model for edit */
    loadingEdit?: boolean;
}

const IngestionEntityModal: React.FC<Props> = ({ onClose, onSubmit, submitting = false, submitError = null, initialData = null, editId = null, loadingEdit = false }) => {
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [showSchemaDiscovery, setShowSchemaDiscovery] = useState(false);
    const [showQueryBuilder, setShowQueryBuilder] = useState(false);
    const [schemaData, setSchemaData] = useState<any[]>([]);
    const [loadingSchema, setLoadingSchema] = useState(false);
    const [schemaError, setSchemaError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        sourceName: '',
        description: '',
        sourceType: '',
        groupUuid: '',
        connectionDetails: '',
        tags: [] as string[],
        isActive: true
    });
    const [tagInput, setTagInput] = useState('');
    const [sourceDefinitionMode, setSourceDefinitionMode] = useState<SourceDefinitionMode>('schema');

    const [metadataPreview, setMetadataPreview] = useState<string | null>(null);


    const [connections, setConnections] = useState<any[]>([]);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [groups, setGroups] = useState<DataModelGroup[]>([]);
    const [loadingGroups, setLoadingGroups] = useState(false);



    useEffect(() => {
        const fetchConnections = async () => {
            setLoadingConnections(true);
            try {
                const data = await apiService.get('/api/DataStores', {}, 'backend');
                setConnections(data.datastores || []);
            } catch (err) {
                console.error('Error fetching connections:', err);
            } finally {
                setLoadingConnections(false);
            }
        };

        fetchConnections();
    }, []);

    useEffect(() => {
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const response = await apiService.get('/api/datastores/groups', {}, 'backend');
                const list = Array.isArray(response) ? response : Array.isArray(response?.groups) ? response.groups : [];
                setGroups(list);
            } catch (err) {
                console.error('Error fetching groups:', err);
                setGroups([]);
            } finally {
                setLoadingGroups(false);
            }
        };

        fetchGroups();
    }, []);

    const resolveGroupUuid = (data: DataModelResponse) =>
        data.groupUuid ?? data.group_uuid ?? '';

    // Prefill form when editing: do not block on connections (so groupUuid and metadata show immediately)
    useEffect(() => {
        if (!initialData) return;
        const meta = initialData.metadataStructure;
        const metaStr = typeof meta === 'string' ? meta : (meta != null ? JSON.stringify(meta, null, 2) : '');
        const conn =
            connections.length > 0 && initialData.type
                ? connections.find(c => c.id === initialData!.type)
                : null;
        setFormData({
            sourceName: initialData.modelName ?? '',
            description: initialData.description ?? '',
            sourceType: conn?.name ?? initialData.type ?? '',
            groupUuid: resolveGroupUuid(initialData),
            connectionDetails: metaStr,
            tags: Array.isArray(initialData.tags) ? initialData.tags : [],
            isActive: initialData.status === 'Active'
        });
        setMetadataPreview(metaStr || null);
    }, [initialData, connections]);
    const handleRemoveTag = (tag: string) => {
        setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
    };

    const handleAddTag = () => {
        const value = tagInput.trim();
        if (value && !formData.tags.includes(value)) {
            setFormData(prev => ({ ...prev, tags: [...prev.tags, value] }));
            setTagInput('');
        }
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const buildPayload = (): IngestionEntityPayload => {
        const selectedConn = connections.find(c => c.name === formData.sourceType);
        return {
            entityName: formData.sourceName,
            description: formData.description,
            sourceTypeUid: selectedConn?.id ?? '',
            groupUuid: formData.groupUuid || '',
            tags: formData.tags,
            metadataStructure: metadataPreview ?? formData.connectionDetails ?? '',
            isActive: formData.isActive
        };
    };

    const getSelectedConnection = useCallback(() => {
        return connections.find(c => c.name === formData.sourceType);
    }, [connections, formData.sourceType]);

    const selectedConnection = getSelectedConnection();
    const isStructuredDb = isStructuredDatabase(selectedConnection?.connectorKey);

    useEffect(() => {
        if (!isStructuredDb && sourceDefinitionMode === 'custom_query') {
            setSourceDefinitionMode('schema');
        }
    }, [isStructuredDb, sourceDefinitionMode]);

    const handleSchemaDiscovery = async () => {
        if (!formData.sourceType) return;

        const selectedConn = getSelectedConnection();
        if (!selectedConn) return;

        setLoadingSchema(true);
        try {
            const data = await apiService.get(`/api/DataStores/${selectedConn.id}/discover`, {}, 'backend');
            setSchemaData(data.nodes || []);
            setShowSchemaDiscovery(true);
        } catch (err) {
            console.error('Schema discovery failed:', err);
        } finally {
            setLoadingSchema(false);
        }
    };

    const handleExpandNode = useCallback(async (nodePath: string) => {
        const selectedConn = getSelectedConnection();
        if (!selectedConn) return [];
        const data = await apiService.get(
            `/api/DataStores/${selectedConn.id}/expand?path=${encodeURIComponent(nodePath)}`,
            {},
            'backend'
        );
        return data.nodes || [];
    }, [getSelectedConnection]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <div className="relative w-full max-w-4xl bg-neutral-900/95 border border-neutral-700/80 rounded-xl shadow-2xl shadow-black/40 overflow-hidden ring-1 ring-white/5 flex flex-col max-h-[90vh]">

                {/* Header - compact */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-neutral-800/80 bg-gradient-to-r from-neutral-900 to-neutral-900/80 shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                            <Database className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div>
                            <h2 className="text-white font-semibold text-sm">{editId ? 'Edit data model' : 'Ingestion Entity'}</h2>
                            <p className="text-[11px] text-gray-500">{editId ? 'Update model details' : 'Configure data source'}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content - two columns: form left, metadata template right */}
                <div className="flex flex-1 min-h-0 p-4 gap-4">
                    {/* Left: form */}
                    <div className="flex flex-col gap-4 w-[380px] shrink-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
                        {editId && loadingEdit ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <span className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                <p className="text-gray-500 text-sm">Loading data model...</p>
                            </div>
                        ) : (
                            <>
                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Source Name</label>
                            <input
                                type="text"
                                placeholder="e.g. Sales DB"
                                className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none transition-all"
                                value={formData.sourceName}
                                onChange={e => setFormData(prev => ({ ...prev, sourceName: e.target.value }))}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Source Type *</label>
                            <div className="relative">
                                <select
                                    className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 pr-9 text-sm text-white focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none appearance-none cursor-pointer transition-all"
                                    value={formData.sourceType}
                                    onChange={e => {
                                        const selectedConn = connections.find(c => c.name === e.target.value);
                                        setFormData(prev => ({
                                            ...prev,
                                            sourceType: e.target.value,
                                            description: selectedConn?.description ?? prev.description
                                        }));
                                    }}
                                >
                                    <option value="" disabled>{loadingConnections ? 'Loading...' : 'Select connection...'}</option>
                                    {connections.map(conn => (
                                        <option key={conn.id} value={conn.name}>
                                            {conn.name} ({conn.connectorKey})
                                        </option>
                                    ))}
                                </select>
                                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 text-gray-500 pointer-events-none" />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Group</label>
                            <div className="relative">
                                <select
                                    className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 pr-9 text-sm text-white focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none appearance-none cursor-pointer transition-all"
                                    value={formData.groupUuid}
                                    onChange={e => setFormData(prev => ({ ...prev, groupUuid: e.target.value }))}
                                >
                                    <option value="" disabled={loadingGroups}>
                                        {loadingGroups ? 'Loading groups...' : 'Select group (optional)'}
                                    </option>
                                    {groups.map(group => (
                                        <option key={group.uuid} value={group.uuid}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 text-gray-500 pointer-events-none" />
                            </div>
                        </div>

                        {formData.sourceType && (
                            <div className="space-y-1">
                                <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Source definition</label>
                                <div className="relative">
                                    <select
                                        className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg pl-9 pr-9 py-2 text-sm text-white focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none appearance-none cursor-pointer transition-all"
                                        value={sourceDefinitionMode}
                                        onChange={e => setSourceDefinitionMode(e.target.value as SourceDefinitionMode)}
                                    >
                                        <option value="schema">Schema</option>
                                        <option value="custom_query" disabled={!isStructuredDb}>
                                            Custom query{!isStructuredDb ? ' (SQL sources only)' : ''}
                                        </option>
                                    </select>
                                    {sourceDefinitionMode === 'schema' ? (
                                        <TableProperties className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400/80 pointer-events-none" />
                                    ) : (
                                        <Terminal className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400/80 pointer-events-none" />
                                    )}
                                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 text-gray-500 pointer-events-none" />
                                </div>
                            </div>
                        )}

                        {formData.sourceType && (
                            <div className="flex items-center gap-2 py-1 flex-wrap">
                                <button
                                    onClick={handleSchemaDiscovery}
                                    disabled={loadingSchema}
                                    className="inline-flex items-center gap-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 px-3 py-1.5 rounded-lg border border-indigo-500/30 text-xs font-medium transition-all hover:border-indigo-500/50 disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {loadingSchema ? (
                                        <span className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Search className="w-3.5 h-3.5" />
                                    )}
                                    Schema Discovery
                                </button>
                                {isStructuredDb && (
                                    <button
                                        type="button"
                                        onClick={() => setShowQueryBuilder(true)}
                                        className="inline-flex items-center gap-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded-lg border border-cyan-500/30 text-xs font-medium transition-all hover:border-cyan-500/50"
                                    >
                                        <Terminal className="w-3.5 h-3.5" />
                                        Query builder
                                    </button>
                                )}
                                {sourceDefinitionMode === 'schema' && (
                                    <button
                                        type="button"
                                        onClick={() => window.open(`${window.location.origin}/schema-enrichment-workspace.html`, '_blank', 'noopener,noreferrer')}
                                        title="Open Schema Enrichment Workspace simulation"
                                        className="inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-lg border border-amber-500/30 text-xs font-medium transition-all hover:border-amber-500/50"
                                    >
                                        <Sparkles className="w-3.5 h-3.5" />
                                        Discover & Enrich Schema
                                    </button>
                                )}
                                <span className="text-[10px] text-gray-500">Select object to auto-fill details</span>
                            </div>
                        )}

                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Description</label>
                            <textarea
                                placeholder="Brief description of the source"
                                className="w-full bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 min-h-[72px] focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none resize-y custom-scrollbar transition-all"
                                value={formData.description}
                                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Tags</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Type and press Enter to add"
                                    className="flex-1 bg-neutral-800/80 border border-neutral-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:ring-1 focus:ring-indigo-500/60 focus:border-indigo-500/40 outline-none transition-all"
                                    value={tagInput}
                                    onChange={e => setTagInput(e.target.value)}
                                    onKeyDown={handleTagInputKeyDown}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddTag}
                                    className="px-3 py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 rounded-lg border border-indigo-500/30 text-sm font-medium transition-all"
                                >
                                    Add
                                </button>
                            </div>
                            {formData.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                    {formData.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 bg-indigo-500/15 text-indigo-300 text-[11px] px-2 py-0.5 rounded-md border border-indigo-500/25"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                className="hover:text-white transition-colors"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between py-1">
                            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Active</label>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={formData.isActive}
                                onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-neutral-900 ${formData.isActive ? 'bg-indigo-600 border-indigo-500' : 'bg-neutral-700 border-neutral-600'}`}
                            >
                                <span
                                    className={`pointer-events-none absolute inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-all top-0.5 ${formData.isActive ? 'left-6' : 'left-0.5'}`}
                                />
                            </button>
                        </div>
                            </>
                        )}
                    </div>

                    {/* Right: Expected metadata structure */}
                    <div className="flex-1 min-w-[320px] flex flex-col pl-4">
                        <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5 shrink-0 mb-1.5">
                            <FileJson className="w-3.5 h-3.5 text-amber-400/80" />
                            Metadata structure
                        </label>
                        <div className="flex-1 min-h-0 rounded-lg border border-neutral-700/50 bg-neutral-950 overflow-hidden flex flex-col">
                            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto custom-scrollbar">
                                {metadataPreview ? (
                                    <SyntaxHighlighter
                                        language="json"
                                        style={vscDarkPlus}
                                        customStyle={{ margin: 0, padding: '0.75rem 1rem', background: 'transparent', fontSize: '11px', lineHeight: 1.4, minHeight: '100%' }}
                                        showLineNumbers={false}
                                        codeTagProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
                                    >
                                        {metadataPreview}
                                    </SyntaxHighlighter>
                                ) : (
                                    <div className="p-4 flex items-center justify-center h-full min-h-[200px] text-gray-500 text-sm">
                                        Use Schema Discovery and select objects from the tree to build a metadata template. It will appear here.
                                    </div>
                                )}
                            </div>
                        </div>
                        {metadataPreview && (
                            <p className="text-[10px] text-gray-500 mt-1.5 shrink-0">This is the metadata model generated from your current schema selection.</p>
                        )}
                    </div>
                </div>

                {submitError && (
                    <div className="px-4 py-2 mx-4 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm flex items-center gap-2">
                        <span>{submitError}</span>
                    </div>
                )}

                {/* Footer - compact */}
                <div className="px-4 py-3 bg-neutral-900/50 flex justify-end items-center gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="text-gray-400 hover:text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => onSubmit(buildPayload())}
                        disabled={submitting}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-4 py-1.5 rounded-lg shadow-lg shadow-indigo-900/30 transition-all disabled:opacity-70 disabled:pointer-events-none inline-flex items-center gap-2 min-w-[120px] justify-center"
                    >
                        {submitting ? (
                            <>
                                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Creating...
                            </>
                        ) : editId ? (
                            'Update'
                        ) : (
                            'Create Source'
                        )}
                    </button>
                </div>

            </div>

            {showTypeSelector && (
                <SourceTypeSelectorModal
                    onClose={() => setShowTypeSelector(false)}
                    onSelect={(type) => {
                        setFormData({ ...formData, sourceType: type });
                        setShowTypeSelector(false);
                    }}
                    currentSelection={formData.sourceType}
                />
            )}
            {showSchemaDiscovery && (
                <SchemaDiscoveryModal
                    onClose={() => setShowSchemaDiscovery(false)}
                    schemaData={schemaData}
                    onExpand={handleExpandNode}
                    sourceType={getSelectedConnection()?.connectorKey}
                    onSelect={(paths) => {
                        // Keep a simple summary in connectionDetails if metadata generation fails
                        if (!paths.length) return;
                        setFormData(prev => ({
                            ...prev,
                            connectionDetails: prev.connectionDetails || paths.join(', ')
                        }));
                    }}
                    onBuildMetadata={(json) => {
                        setFormData(prev => ({
                            ...prev,
                            connectionDetails: json
                        }));
                        setMetadataPreview(json);
                        setShowSchemaDiscovery(false);
                    }}
                />
            )}
            {showQueryBuilder && getSelectedConnection() && (
                <QueryBuilderModal
                    onClose={() => setShowQueryBuilder(false)}
                    datastoreId={getSelectedConnection()!.id}
                    connectionName={getSelectedConnection()!.name || formData.sourceType}
                    onSubmitTemplate={(json) => {
                        setFormData(prev => ({
                            ...prev,
                            connectionDetails: json
                        }));
                        setMetadataPreview(json);
                        setShowQueryBuilder(false);
                    }}
                />
            )}
        </div>
    );
};

export default IngestionEntityModal;
